import React, { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

// Selector de imagen con recorte, compartido por la foto del aspirante y el logo
// de la entidad. Salió de ApplicantPhotoUpload al necesitarse una segunda vez;
// lo único que cambiaba entre las dos era la forma y la proporción.
//
// Las páginas públicas de /submission no tienen sesión de Firebase Auth, así que
// el archivo recortado se le entrega al padre y sube con el resto del formulario
// por el intake — nunca por el SDK de Storage.
//
// Usa su PROPIO input oculto y no generic/UploadFile, que tiene el id
// "upload-file" hardcodeado y chocaría con cualquier otro selector de la página.
//
// Props:
//   currentUrl   imagen guardada, si hay
//   aspect       proporción del recorte (1 = cuadrado, 16/9 = apaisado)
//   shape        "circle" | "square" — sólo apariencia
//   outputWidth  ancho en píxeles del archivo resultante; el alto sale de aspect
//   outputName   nombre del File que se entrega
//   emptyIcon    qué pintar cuando no hay imagen
//   onChange(file) / onRemove()

const JPEG_QUALITY = 0.85;
// Guarda sobre el archivo ESCOGIDO, antes de leerlo a memoria. Lo que sale del
// recorte se re-codifica muy por debajo del tope del backend.
const MAX_PICK_BYTES = 10 * 1024 * 1024;

// Dibuja el rectángulo escogido en un canvas y lo re-codifica como JPEG.
// `pixelCrop` viene de react-easy-crop, ya en píxeles de la imagen original.
function getCroppedImage(imageSrc, pixelCrop, { width, height, name }) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onerror = () => reject(new Error("IMAGE_LOAD_FAILED"));
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      // JPEG no tiene canal alfa: se rellena primero para que un PNG con
      // transparencia no salga negro. Importa sobre todo en los logos, que casi
      // siempre vienen en PNG transparente.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, width, height
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error("CANVAS_EMPTY")); return; }
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        JPEG_QUALITY
      );
    };
    image.src = imageSrc;
  });
}

export default function ImageCropUpload({
  currentUrl,
  alt = "",
  disabled = false,
  aspect = 1,
  shape = "circle",
  outputWidth = 512,
  outputName = "image.jpg",
  label = "Imagen",
  dialogTitle = "Ajusta la imagen",
  confirmLabel = "Usar esta imagen",
  removeLabel = "Quitar imagen",
  emptyIcon = <ImageOutlinedIcon sx={{ fontSize: "3rem" }} />,
  changeAriaLabel = "Cambiar imagen",
  onChange,
  onRemove,
}) {
  const inputRef = useRef(null);
  const [rawImage, setRawImage] = useState(null); // data URL en edición
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const outputHeight = Math.round(outputWidth / aspect);
  const round = shape === "circle";
  // Un logo apaisado se ve mal en un avatar redondo, así que la caja crece a lo
  // ancho siguiendo la misma proporción del recorte.
  const boxHeight = 112;
  const boxWidth = round ? boxHeight : Math.round(boxHeight * aspect);

  // El estado del recorte se reinicia cada vez que entra una imagen nueva.
  useEffect(() => {
    if (!rawImage) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, [rawImage]);

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    // Se limpia el input para que escoger el mismo archivo dos veces seguidas
    // siga disparando onChange.
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Selecciona una imagen (JPG o PNG).");
      return;
    }
    if (file.size > MAX_PICK_BYTES) {
      setError(`La imagen supera ${Math.round(MAX_PICK_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setRawImage(String(reader.result));
    reader.onerror = () => setError("No fue posible leer la imagen.");
    reader.readAsDataURL(file);
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setWorking(true);
    try {
      const file = await getCroppedImage(rawImage, croppedAreaPixels, {
        width: outputWidth,
        height: outputHeight,
        name: outputName,
      });
      onChange(file);
      setRawImage(null);
    } catch (e) {
      setError("No fue posible procesar la imagen. Intenta con otra.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Stack spacing={1} alignItems="center">
      <Badge
        overlap={round ? "circular" : "rectangular"}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            aria-label={changeAriaLabel}
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <PhotoCameraIcon fontSize="small" />
          </IconButton>
        }
      >
        <Avatar
          alt={alt}
          src={currentUrl || undefined}
          variant={round ? "circular" : "rounded"}
          // `contain` y no `cover`: un logo recortado por los bordes deja de ser
          // el logo. La foto de perfil sí va recortada, pero ahí el recorte ya
          // lo hizo el usuario en el diálogo.
          imgProps={round ? undefined : { style: { objectFit: "contain" } }}
          sx={{
            width: boxWidth,
            height: boxHeight,
            bgcolor: currentUrl && !round ? "grey.100" : "primary.main",
          }}
        >
          {!currentUrl && emptyIcon}
        </Avatar>
      </Badge>
      <input ref={inputRef} hidden type="file" accept="image/*" onChange={handlePick} />

      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {currentUrl && onRemove && (
        <Button size="small" color="inherit" disabled={disabled} onClick={onRemove}>
          {removeLabel}
        </Button>
      )}
      {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

      <Dialog open={!!rawImage} onClose={() => !working && setRawImage(null)} fullWidth maxWidth="xs">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ position: "relative", width: "100%", height: 300, bgcolor: "grey.900", borderRadius: 1, overflow: "hidden" }}>
            <Cropper
              image={rawImage}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={round ? "round" : "rect"}
              showGrid={!round}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">Zoom</Typography>
            <Slider value={zoom} min={1} max={4} step={0.1} onChange={(_, v) => setZoom(v)} sx={{ flex: 1 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawImage(null)} disabled={working}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={working || !croppedAreaPixels}>
            {working ? "Procesando…" : confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
